import {
  ACTION,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableFilters,
  Box,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  Flex,
  FormControlSizes,
  LedgerAccountTypeEnum,
  MixpanelEvent,
  MixpanelEventSource,
  Panel,
  PANEL_HEADER_HEIGHT,
  PanelActions,
  PanelContent,
  PanelHeader,
  Text,
  useBlotterTable,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  usePortal,
  type AgGridMenuItems,
  type IHedgeRule,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useState } from 'react';
import { usePositionAutoHedgingRulesDrawer } from '../../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useRoleAuth } from '../../../../hooks';
import { useGetOpenTransactionsMenuItems } from '../AccountLedgerEvents/useGetOpenTransactionsMenuItems';
import { useHedgeRuleControlsColumns } from './columns';
import { usePositionAutoHedgingRules } from './usePositionAutoHedgingRules';

export function useGetPositionAutoHedgingRulesMenu() {
  const { isAuthorized } = useRoleAuth();

  const getOpenTransactionsMenuItems = useGetOpenTransactionsMenuItems();

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<IHedgeRule>) => {
    const items: AgGridMenuItems = [];
    const hedgeRule = params.node?.data;
    const hedgeRuleSubAccountName = hedgeRule && params.context.current.subAccountsByID?.get(hedgeRule.AccountID)?.Name;
    if (isAuthorized(ACTION.VIEW_LEDGER_EVENTS) && hedgeRule && hedgeRuleSubAccountName) {
      items.push(
        ...getOpenTransactionsMenuItems({
          asset: hedgeRule.Asset,
          account: hedgeRuleSubAccountName,
          accountType: LedgerAccountTypeEnum.SubAccount,
          source: MixpanelEventSource.PositionAutoHedging,
        })
      );
    }
    return items;
  });

  return {
    getCustomContextMenuItems,
  };
}

export function PositionAutoHedgingRules({ tabId }: { tabId: string }) {
  const mixpanel = useMixpanel();
  const cols = useHedgeRuleControlsColumns();
  const { getCustomContextMenuItems } = useGetPositionAutoHedgingRulesMenu();
  const [quickFilterText, setQuickFilterText] = useState<string>('');
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  const persistedBlotterTable = usePersistedBlotterTable(`PAH-Tab/${tabId}`);

  const { dataObs, filterBuilderAccordion } = usePositionAutoHedgingRules({
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    initialState: persistedBlotterTable.initialState,
  });
  const blotterTable = useBlotterTable({
    dataObservable: dataObs,
    errorObservable: undefined,
    rowID: 'HedgeRuleID' as keyof IHedgeRule,
    quickSearchParams: {
      filterText: quickFilterText,
    },
    persistence: persistedBlotterTable,
    columns: cols,
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    gridOptions: {
      onRowDoubleClicked: ({ data }) =>
        data != null && openPositionAutoHedgingRulesDrawer({ hedgeRuleID: data.HedgeRuleID }),
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
    },
  });
  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Panel data-testid="position-auto-hedging-rules">
      <PanelHeader minHeight={PANEL_HEADER_HEIGHT / 2 + 'px'} px="spacing16">
        <Flex gap="spacing12" alignItems="flex-end">
          <h2>Rules</h2>
          <Text fontSize="fontSizeSmall">Position Autohedging</Text>
        </Flex>

        <PanelActions>
          <Box ref={filtersContainerRef} />
          <Button
            data-testid="create-rule-button"
            variant={ButtonVariants.Positive}
            size={FormControlSizes.Small}
            onClick={() => {
              mixpanel.track(MixpanelEvent.ClickCreateNewAutohedgingRule);
              openPositionAutoHedgingRulesDrawer();
            }}
          >
            Create Rule
          </Button>
        </PanelActions>
      </PanelHeader>
      <PanelContent pb={0} px="spacing16">
        <BlotterTableFilters {...filterBuilderAccordion} onQuickFilterTextChanged={setQuickFilterText} />
        <BlotterTable {...blotterTable} />
      </PanelContent>
    </Panel>
  );
}
